import type { ReactElement } from 'react';
import { MessageCircle } from 'lucide-react';
import { Avatar, profileName } from '@/components';
import { cn } from '@/lib/cn';
import type { CommentTypingEntry } from '@/lib/comment-typing';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface CommentTypingIndicatorProps {
  readonly typingUsers: readonly CommentTypingEntry[];
}

export function CommentTypingIndicator({
  typingUsers,
}: CommentTypingIndicatorProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  if (typingUsers.length === 0) return null;

  const names = typingUsers.map((entry) => profileName(entry.user));
  const label = t('cardBack.comments.typing', { names: names.join(', ') }, names.length);

  return (
    <div role="status" aria-label={label} className="flex min-h-8 items-center gap-1.5">
      <span className="inline-flex items-center" aria-hidden="true">
        {typingUsers.map((entry, index) => (
          <span
            key={entry.user.id}
            className={cn('relative inline-flex', index > 0 && '-ml-2')}
          >
            <Avatar profile={entry.user} size="sm" decorative />
            <span className="absolute -bottom-0.5 -right-1 inline-flex size-4 items-center justify-center rounded-full border border-surface-raised bg-accent text-accent-ink">
              <MessageCircle aria-hidden="true" className="size-2.5" />
            </span>
          </span>
        ))}
      </span>
      <span className="sr-only">{label}</span>
    </div>
  );
}
